import { useCallback, useEffect, useReducer, useState } from 'react';
import LocationDropdowns from '../LocationDropdowns';
import { AdminUserBasicInfoSection, AdminUserAdditionalContactFields } from '../AdminUserForm';
import {
  initialFormData,
  formReducer,
  EducationFields,
  PlacementFields,
  CourseSelection,
  MarksFields,
  useFormData,
} from '../AdminAddUserForm';
import { mapUserStudentToEditForm } from '../../utils/mapUserStudentToEditForm';
import { batchesAPI, studentsAPI, usersAPI } from '../../services/api';
import { BATCH_STATUS_OPTIONS } from '../../domain/enrollment/constants';
import SearchableSelect from '../Form/SearchableSelect';
import useTrainingCenters, { centerOptionsWithFallback } from '../../hooks/useTrainingCenters';
import { scrollToAdminUserFormSection } from '../../domain/adminUser/adminUserFormSections';
import { STAGING_EDIT_SECTION_IDS } from '../../domain/studentCompleteness/completenessFocusTarget';
import '../../styles/admin-user-form-nav.css';

const PLACEMENT_STATUS_OPTIONS = [
  'Placed',
  'In progress',
  'Hold',
  'Under Preparation',
  'Not interested',
];

const YES_NO = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

const PROFILE_PREFERENCE_OPTIONS = [
  { value: 'Physical_Design', label: 'Physical Design' },
  { value: 'Analog_Design', label: 'Analog Design' },
  { value: 'DV_Profile', label: 'DV Profile' },
  { value: 'Other', label: 'Other' },
];

const JOINING_KIT_ADDRESS_OPTIONS = [
  { value: 'current', label: 'Current' },
  { value: 'permanent', label: 'Permanent' },
];

const MODE_OF_COURSE_OPTIONS = [
  { value: 'Online', label: 'Online' },
  { value: 'Offline', label: 'Offline' },
  { value: 'Hybrid', label: 'Hybrid' },
];

const heardFromOptionValue = (option) => (typeof option === 'object' ? option.id : option);
const heardFromOptionLabel = (option) =>
  typeof option === 'object' ? option.value || option.name : option;

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

function CollapsibleSection({ id, title, defaultOpen = false, forceOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen || forceOpen);
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);
  return (
    <div
      id={id || undefined}
      className={`border border-gray-200 rounded-lg overflow-hidden${id ? ' auf-form-section' : ''}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
      >
        <span className="font-semibold text-sm text-gray-800">{title}</span>
        <span className="text-gray-500">{open ? '▼' : '▶'}</span>
      </button>
      {open && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
}

function InlineField({ label, fieldName, highlight = false, children }) {
  return (
    <label
      className={`block text-sm${highlight ? ' staging-focus-field ring-2 ring-red-400/70 rounded-md p-1 -m-1 bg-red-50/60' : ''}`}
      data-form-field={fieldName || undefined}
    >
      <span className="font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function highlightFormFields(formFields = []) {
  if (!formFields.length) return;
  for (const name of formFields) {
    const byData = document.querySelector(`[data-form-field="${name}"]`);
    const byName =
      document.querySelector(`#staging-full-student-form [name="${name}"]`) ||
      document.querySelector(`[name="${name}"]`);
    const el = byData || byName;
    if (!el) continue;
    el.classList.remove('auf-section-pulse', 'staging-focus-field-pulse');
    void el.offsetWidth;
    el.classList.add('auf-section-pulse', 'staging-focus-field-pulse');
    if (typeof byName?.focus === 'function') {
      try {
        byName.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
    }
  }
}

/**
 * Full inline student edit — all fields from Edit User page + interview readiness & extra enrollments.
 * Pass `preloadFormData` to skip API load (e.g. data staging review).
 */
const StudentDetailInlineEdit = ({
  student,
  token,
  onSave,
  onCancel,
  saving = false,
  error = null,
  preloadFormData = null,
  /** Stable key (e.g. staging doc id) so preload reloads only when selection changes */
  preloadKey = null,
  /** When preloading an existing SQL student (Edit User parity) */
  initialUserId = null,
  getFieldStyle: getFieldStyleProp = null,
  guideSlot = null,
  actionSlot = null,
  hideDefaultActions = false,
  formClassName = 'mt-6 border-t pt-6 px-4 space-y-4',
  formId = undefined,
  /** @type {{ sectionId: string, formFields: string[], label?: string } | null} */
  focusTarget = null,
}) => {
  const [formData, dispatch] = useReducer(formReducer, initialFormData);
  const [loadError, setLoadError] = useState(null);
  const [loadingForm, setLoadingForm] = useState(!preloadFormData);
  const [userId, setUserId] = useState(initialUserId || null);

  const {
    roles,
    educationOptions,
    universityOptions,
    companyOptions,
    heardFromOptions,
    refreshCompanyOptions,
    courses,
    coursesLoading,
    courseCategories,
    domains,
    batches,
    counselors,
  } = useFormData(token);
  const { options: catalogCenters } = useTrainingCenters();

  const getFieldStyle = useCallback(
    (value, isRequired = false, fieldName) => {
      if (typeof getFieldStyleProp === 'function') {
        return getFieldStyleProp(value, isRequired, fieldName);
      }
      return {};
    },
    [getFieldStyleProp]
  );

  const handleBatchSelection = async (batchId) => {
    if (!batchId) return;
    try {
      const response = await batchesAPI.getById(batchId);
      const batch = response.data?.data || response.data;
      if (!batch) return;
      if (batch.mode) {
        dispatch({ type: 'UPDATE_FIELD', field: 'modeOfCourse', value: batch.mode });
      }
      if (batch.mode === 'Offline' && batch.location) {
        dispatch({ type: 'UPDATE_FIELD', field: 'locationOfCenter', value: batch.location });
      }
      if (batch.courseId) {
        dispatch({ type: 'UPDATE_FIELD', field: 'courseId', value: String(batch.courseId) });
        const categoryId = batch.course?.categoryId || batch.course?.category?.id;
        if (categoryId) {
          dispatch({
            type: 'UPDATE_FIELD',
            field: 'courseCategoryId',
            value: String(categoryId),
          });
        }
      }
      if (batch.startDate) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'batchStartDate',
          value: String(batch.startDate).split('T')[0],
        });
      }
      const end =
        batch.expectedCompletionDate || batch.tentativeEndDate || batch.endDate;
      if (end) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'courseCompletionDate',
          value: String(end).split('T')[0],
        });
      }
    } catch (err) {
      console.warn('Batch auto-fill failed', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'UPDATE_FIELD', field: name, value });
    if (name === 'batchId') {
      handleBatchSelection(value);
    }
    if (name === 'diplomaDone' && value === 'No') {
      dispatch({ type: 'UPDATE_FIELD', field: 'semester', value: '' });
    }
  };

  const handleNestedChange = (section, field, value) => {
    dispatch({ type: 'UPDATE_NESTED_FIELD', section, field, value });
  };

  useEffect(() => {
    if (preloadKey != null && preloadFormData) {
      dispatch({
        type: 'LOAD_DATA',
        data: { ...initialFormData, ...preloadFormData, role: 'student' },
      });
      setLoadingForm(false);
      setLoadError(null);
      setUserId(initialUserId || null);
      return undefined;
    }

    if (preloadFormData && preloadKey == null) {
      dispatch({
        type: 'LOAD_DATA',
        data: { ...initialFormData, ...preloadFormData, role: 'student' },
      });
      setLoadingForm(false);
      setLoadError(null);
      setUserId(initialUserId || null);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      if (!student?.id) return;
      const email = student.emailAddress || student.email;
      if (!email) {
        setLoadError('Student email missing');
        setLoadingForm(false);
        return;
      }
      setLoadingForm(true);
      setLoadError(null);
      try {
        const [userRes, studentRes] = await Promise.all([
          usersAPI.getByEmail(email),
          studentsAPI.getById(student.id),
        ]);
        if (cancelled) return;
        const user = userRes.data;
        setUserId(user?.id || user?._id);
        const mapped = mapUserStudentToEditForm(user, studentRes.data);
        dispatch({ type: 'LOAD_DATA', data: mapped });
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.response?.data?.message || err.message || 'Failed to load student');
        }
      } finally {
        if (!cancelled) setLoadingForm(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preloadFormData read when preloadKey changes
  }, [student?.id, student?.emailAddress, student?.email, preloadKey, initialUserId]);

  useEffect(() => {
    if (loadingForm || !focusTarget?.sectionId) return undefined;
    const t = window.setTimeout(() => {
      scrollToAdminUserFormSection(focusTarget.sectionId);
      highlightFormFields(focusTarget.formFields || []);
    }, 120);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when selection / focus field changes
  }, [loadingForm, preloadKey, focusTarget?.sectionId, focusTarget?.formFields?.join(',')]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, { userId, companyOptions, refreshCompanyOptions });
  };

  if (loadingForm) {
    return (
      <div className="mt-6 border-t pt-6 px-4 text-sm text-gray-500">
        Loading editable student details…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mt-6 border-t pt-6 px-4 text-sm text-red-600">
        {loadError}
      </div>
    );
  }

  const isStudentRole = true;
  const focusFieldSet = new Set(focusTarget?.formFields || []);
  const sectionForce = (sectionId) => focusTarget?.sectionId === sectionId;
  const fieldHighlight = (name) => focusFieldSet.has(name);

  return (
    <form
      id={formId}
      onSubmit={handleSubmit}
      className={formClassName}
      onClick={(e) => e.stopPropagation()}
    >
      {!hideDefaultActions && (
        <div className="flex flex-wrap items-center justify-between gap-3 pb-2 border-b">
          <div>
            <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide">
              Edit all student details
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Same fields as Edit User — personal, academic, enrollment, placement, counselling,
              interview readiness.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save all changes'}
            </button>
          </div>
        </div>
      )}

      {guideSlot}

      {(error || loadError) && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error || loadError}
        </div>
      )}

      <CollapsibleSection
        id={STAGING_EDIT_SECTION_IDS.personal}
        title="👤 Personal & contact"
        defaultOpen
        forceOpen={sectionForce(STAGING_EDIT_SECTION_IDS.personal)}
      >
        <AdminUserBasicInfoSection
          layoutVariant="edit"
          formData={formData}
          onChange={handleChange}
          roles={roles}
          isStudentRole={isStudentRole}
          getFieldStyle={getFieldStyle}
        />
        <AdminUserAdditionalContactFields
          formData={formData}
          onChange={handleChange}
          getFieldStyle={getFieldStyle}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          <InlineField label="Date of birth" fieldName="dob" highlight={fieldHighlight('dob')}>
            <input type="date" name="dob" className={inputClass} value={formData.dob || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Pincode" fieldName="pincode" highlight={fieldHighlight('pincode')}>
            <input name="pincode" className={inputClass} value={formData.pincode || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Employee ID" fieldName="employeeId" highlight={fieldHighlight('employeeId')}>
            <input name="employeeId" className={inputClass} value={formData.employeeId || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Experience (years)">
            <input name="experienceYears" className={inputClass} value={formData.experienceYears || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Availability">
            <input name="availability" className={inputClass} value={formData.availability || ''} onChange={handleChange} />
          </InlineField>
          <InlineField
            label="Profile preference"
            fieldName="profilePreference"
            highlight={fieldHighlight('profilePreference')}
          >
            <SearchableSelect
              name="profilePreference"
              className="w-full"
              value={formData.profilePreference || ''}
              onChange={handleChange}
              options={PROFILE_PREFERENCE_OPTIONS}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Has diploma">
            <SearchableSelect
              name="diplomaDone"
              className="w-full"
              value={formData.diplomaDone || ''}
              onChange={handleChange}
              options={YES_NO}
              placeholder="—"
            />
          </InlineField>
          <InlineField
            label="Joining kit received"
            fieldName="joiningKitReceived"
            highlight={fieldHighlight('joiningKitReceived')}
          >
            <SearchableSelect
              name="joiningKitReceived"
              className="w-full"
              value={formData.joiningKitReceived || ''}
              onChange={handleChange}
              options={YES_NO}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Joining kit address">
            <SearchableSelect
              name="joiningKitAddress"
              className="w-full"
              value={formData.joiningKitAddress || ''}
              onChange={handleChange}
              options={JOINING_KIT_ADDRESS_OPTIONS}
              placeholder="—"
            />
          </InlineField>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <InlineField label="Current address" fieldName="address" highlight={fieldHighlight('address')}>
            <textarea name="address" className={`${inputClass} min-h-[72px]`} value={formData.address || ''} onChange={handleChange} />
          </InlineField>
          <InlineField
            label="Permanent address"
            fieldName="permanentAddress"
            highlight={fieldHighlight('permanentAddress')}
          >
            <textarea name="permanentAddress" className={`${inputClass} min-h-[72px]`} value={formData.permanentAddress || ''} onChange={handleChange} />
          </InlineField>
        </div>
        <div className="mt-4">
          <h5 className="text-xs font-bold uppercase text-gray-500 mb-2">Parents / guardian</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InlineField label="Parents name" fieldName="parentsName" highlight={fieldHighlight('parentsName')}>
              <input name="parentsName" className={inputClass} value={formData.parentsName || ''} onChange={handleChange} />
            </InlineField>
            <InlineField label="Parents phone" fieldName="parentsPhone" highlight={fieldHighlight('parentsPhone')}>
              <input name="parentsPhone" className={inputClass} value={formData.parentsPhone || ''} onChange={handleChange} />
            </InlineField>
            <InlineField label="Parents email" fieldName="parentsEmail" highlight={fieldHighlight('parentsEmail')}>
              <input type="email" name="parentsEmail" className={inputClass} value={formData.parentsEmail || ''} onChange={handleChange} />
            </InlineField>
            <InlineField label="Guardian name">
              <input name="guardianName" className={inputClass} value={formData.guardianName || ''} onChange={handleChange} />
            </InlineField>
            <InlineField label="Guardian contact">
              <input name="guardianContact" className={inputClass} value={formData.guardianContact || ''} onChange={handleChange} />
            </InlineField>
            <InlineField label="Guardian email">
              <input type="email" name="guardianEmail" className={inputClass} value={formData.guardianEmail || ''} onChange={handleChange} />
            </InlineField>
          </div>
        </div>
        <div className="mt-4">
          <LocationDropdowns
            selectedCountry={formData.country}
            selectedState={formData.state}
            selectedCity={formData.cityId}
            onCountryChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'country', value })}
            onStateChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'state', value })}
            onCityChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'cityId', value })}
            getFieldStyle={getFieldStyle}
          />
          <InlineField label="How did you hear about us?" fieldName="heardFrom" highlight={fieldHighlight('heardFrom')}>
            <SearchableSelect
              name="heardFrom"
              className="w-full mt-2"
              value={formData.heardFrom || ''}
              onChange={handleChange}
              options={heardFromOptions}
              getOptionValue={heardFromOptionValue}
              getOptionLabel={heardFromOptionLabel}
              placeholder="—"
            />
          </InlineField>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <InlineField label="Bio">
            <textarea name="bio" className={`${inputClass} min-h-[60px]`} value={formData.bio || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Notes">
            <textarea name="notes" className={`${inputClass} min-h-[60px]`} value={formData.notes || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Motivation">
            <textarea name="motivation" className={`${inputClass} min-h-[60px]`} value={formData.motivation || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Resume URL">
            <input name="resumeUrl" className={inputClass} value={formData.resumeUrl || ''} onChange={handleChange} />
          </InlineField>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={STAGING_EDIT_SECTION_IDS.marks}
        title="📊 Academic marks"
        forceOpen={sectionForce(STAGING_EDIT_SECTION_IDS.marks)}
      >
        <MarksFields formData={formData} dispatch={dispatch} />
      </CollapsibleSection>

      <CollapsibleSection
        id={STAGING_EDIT_SECTION_IDS.education}
        title="🎓 Education & gaps"
        forceOpen={sectionForce(STAGING_EDIT_SECTION_IDS.education)}
      >
        <EducationFields
          formData={formData}
          dispatch={dispatch}
          educationOptions={educationOptions}
          universityOptions={universityOptions}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <InlineField label="Education gap">
            <SearchableSelect
              name="educationGap"
              className="w-full"
              value={formData.educationGap || ''}
              onChange={handleChange}
              options={YES_NO}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Gap in education justification">
            <input name="gapInEducationJustification" className={inputClass} value={formData.gapInEducationJustification || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Gap after highest education justification">
            <input name="gapAfterHighestEducationJustification" className={inputClass} value={formData.gapAfterHighestEducationJustification || ''} onChange={handleChange} />
          </InlineField>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={STAGING_EDIT_SECTION_IDS.enrollment}
        title="📚 Course & enrollment"
        forceOpen={sectionForce(STAGING_EDIT_SECTION_IDS.enrollment)}
      >
        <CourseSelection
          formData={formData}
          courses={courses}
          dispatch={dispatch}
          getFieldStyle={getFieldStyle}
          disableWhenBatchSet={!!formData.batchId}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          <InlineField label="Batch" fieldName="batchId" highlight={fieldHighlight('batchId')}>
            <SearchableSelect
              name="batchId"
              className="w-full"
              value={formData.batchId || ''}
              onChange={handleChange}
              options={batches}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Domain" fieldName="domainId" highlight={fieldHighlight('domainId')}>
            <SearchableSelect
              name="domainId"
              className="w-full"
              value={formData.domainId || ''}
              onChange={handleChange}
              options={domains}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Course category">
            <SearchableSelect
              name="courseCategoryId"
              className="w-full"
              value={formData.courseCategoryId || ''}
              onChange={handleChange}
              options={courseCategories}
              disabled={!!formData.courseId || !!formData.batchId}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Batch start date">
            <input type="date" name="batchStartDate" className={inputClass} value={formData.batchStartDate || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Course completion date">
            <input type="date" name="courseCompletionDate" className={inputClass} value={formData.courseCompletionDate || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Mode" fieldName="modeOfCourse" highlight={fieldHighlight('modeOfCourse')}>
            <SearchableSelect
              name="modeOfCourse"
              className="w-full"
              value={formData.modeOfCourse || ''}
              onChange={handleChange}
              options={MODE_OF_COURSE_OPTIONS}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Center location">
            <SearchableSelect
              name="locationOfCenter"
              className="w-full"
              value={formData.locationOfCenter || ''}
              onChange={handleChange}
              creatable
              options={centerOptionsWithFallback(catalogCenters, batches, [formData.locationOfCenter])}
              placeholder="Select Location of Center"
            />
          </InlineField>
          <InlineField label="Student status">
            <SearchableSelect
              name="studentStatus"
              className="w-full"
              value={formData.studentStatus || ''}
              onChange={handleChange}
              options={BATCH_STATUS_OPTIONS}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Placement status">
            <SearchableSelect
              name="placementStatus"
              className="w-full"
              value={formData.placementStatus || ''}
              onChange={handleChange}
              options={PLACEMENT_STATUS_OPTIONS}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Attendance %">
            <input name="attendancePercentage" className={inputClass} value={formData.attendancePercentage || ''} onChange={handleChange} />
          </InlineField>
          <InlineField label="Certification issued">
            <SearchableSelect
              name="certificationIssued"
              className="w-full"
              value={formData.certificationIssued || ''}
              onChange={handleChange}
              options={YES_NO}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Video testimony (enrollment)">
            <SearchableSelect
              name="videoTestimonyReceived"
              className="w-full"
              value={formData.videoTestimonyReceived || ''}
              onChange={handleChange}
              options={YES_NO}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Text testimony (enrollment)">
            <SearchableSelect
              name="testimonyReceived"
              className="w-full"
              value={formData.testimonyReceived || ''}
              onChange={handleChange}
              options={YES_NO}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Insurance taken">
            <SearchableSelect
              name="insuranceTaken"
              className="w-full"
              value={formData.insuranceTaken || ''}
              onChange={handleChange}
              options={YES_NO}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Insurance deactivated">
            <SearchableSelect
              name="insuranceDeactivated"
              className="w-full"
              value={formData.insuranceDeactivated || ''}
              onChange={handleChange}
              options={YES_NO}
              placeholder="—"
            />
          </InlineField>
          <InlineField label="Remarks">
            <textarea name="remarks" className={`${inputClass} min-h-[60px]`} value={formData.remarks || ''} onChange={handleChange} />
          </InlineField>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="💼 Placement">
        <PlacementFields
          formData={formData}
          dispatch={dispatch}
          companyOptions={companyOptions}
        />
      </CollapsibleSection>

      <CollapsibleSection title="💬 Counselling sessions">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(formData.counsellingSessions || []).map((session, idx) => (
            <div key={idx} className="border rounded-md p-3 space-y-2 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600">Session {idx + 1}</p>
              <InlineField label="Date">
                <input
                  type="date"
                  className={inputClass}
                  value={session.sessionDate || ''}
                  onChange={(e) =>
                    dispatch({
                      type: 'UPDATE_COUNSELLING_SESSION',
                      index: idx,
                      field: 'sessionDate',
                      value: e.target.value,
                    })
                  }
                />
              </InlineField>
              <InlineField label="Counselor">
                <SearchableSelect
                  className="w-full"
                  value={session.counselorId || ''}
                  onChange={(e) =>
                    dispatch({
                      type: 'UPDATE_COUNSELLING_SESSION',
                      index: idx,
                      field: 'counselorId',
                      value: e.target.value,
                    })
                  }
                  options={counselors}
                  placeholder="—"
                />
              </InlineField>
              <InlineField label="Remarks">
                <textarea
                  className={`${inputClass} min-h-[48px]`}
                  value={session.remarks || ''}
                  onChange={(e) =>
                    dispatch({
                      type: 'UPDATE_COUNSELLING_SESSION',
                      index: idx,
                      field: 'remarks',
                      value: e.target.value,
                    })
                  }
                />
              </InlineField>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="🎯 Interview readiness">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[
            ['interviewReady', 'Interview ready'],
            ['allModulesComplete', 'All modules complete'],
            ['projectSubmitted', 'Project submitted'],
            ['studentReadiness', 'Student readiness'],
            ['trainerReadiness', 'Trainer readiness'],
            ['resumeReadySubmitted', 'Resume ready & submitted'],
            ['mockInterviewDone', 'Mock interview done'],
            ['inPlacementGroup', 'In placement group'],
            ['inMockGroup', 'In mock group'],
            ['inUnderpreparedGroup', 'In underprepared group'],
          ].map(([key, label]) => (
            <InlineField key={key} label={label}>
              <SearchableSelect
                className="w-full"
                value={formData.interviewReadiness?.[key] || ''}
                onChange={(e) => handleNestedChange('interviewReadiness', key, e.target.value)}
                options={YES_NO}
                placeholder="—"
              />
            </InlineField>
          ))}
          <InlineField label="# Mock interviews">
            <input
              className={inputClass}
              value={formData.interviewReadiness?.numberOfMockInterviews || ''}
              onChange={(e) =>
                handleNestedChange('interviewReadiness', 'numberOfMockInterviews', e.target.value)
              }
            />
          </InlineField>
          {[
            ['communicationSkillsRating', 'Communication rating'],
            ['attitudeRating', 'Attitude rating'],
            ['presentationRating', 'Presentation rating'],
            ['technicalReadinessRating', 'Technical rating'],
            ['feedbackScore', 'Feedback score'],
          ].map(([key, label]) => (
            <InlineField key={key} label={label}>
              <input
                className={inputClass}
                value={formData.interviewReadiness?.[key] || ''}
                onChange={(e) => handleNestedChange('interviewReadiness', key, e.target.value)}
              />
            </InlineField>
          ))}
          <InlineField label="Areas to improve">
            <textarea
              className={`${inputClass} min-h-[60px]`}
              value={formData.interviewReadiness?.areasToImprove || ''}
              onChange={(e) =>
                handleNestedChange('interviewReadiness', 'areasToImprove', e.target.value)
              }
            />
          </InlineField>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="📝 Testimony record">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['videoTestimonyReceived', 'Video testimony received'],
            ['textTestimonyReceived', 'Text testimony received'],
            ['wantToContributeAsTrainer', 'Wants to contribute as trainer'],
          ].map(([key, label]) => (
            <InlineField key={key} label={label}>
              <SearchableSelect
                className="w-full"
                value={formData.testimony?.[key] || ''}
                onChange={(e) => handleNestedChange('testimony', key, e.target.value)}
                options={YES_NO}
                placeholder="—"
              />
            </InlineField>
          ))}
          <InlineField label="Text testimony content">
            <textarea
              className={`${inputClass} min-h-[72px]`}
              value={formData.testimony?.textTestimonyContent || ''}
              onChange={(e) =>
                handleNestedChange('testimony', 'textTestimonyContent', e.target.value)
              }
            />
          </InlineField>
          <InlineField label="Feedback to improve training">
            <textarea
              className={`${inputClass} min-h-[72px]`}
              value={formData.testimony?.feedbackToImproveTraining || ''}
              onChange={(e) =>
                handleNestedChange('testimony', 'feedbackToImproveTraining', e.target.value)
              }
            />
          </InlineField>
        </div>
      </CollapsibleSection>

      {Array.isArray(formData.additionalEnrollments) &&
        formData.additionalEnrollments.length > 0 && (
          <CollapsibleSection title={`📚 Additional enrollments (${formData.additionalEnrollments.length})`}>
            {formData.additionalEnrollments.map((en, idx) => (
              <div key={en.id || idx} className="border rounded-md p-3 mb-3 space-y-3 bg-slate-50">
                <p className="text-sm font-semibold">{en.batchName || `Enrollment ${idx + 2}`}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <InlineField label="Student status">
                    <SearchableSelect
                      className="w-full"
                      value={en.batchStatus || ''}
                      onChange={(e) => {
                        const next = [...formData.additionalEnrollments];
                        next[idx] = { ...next[idx], batchStatus: e.target.value };
                        dispatch({ type: 'UPDATE_FIELD', field: 'additionalEnrollments', value: next });
                      }}
                      options={BATCH_STATUS_OPTIONS}
                      placeholder="—"
                    />
                  </InlineField>
                  <InlineField label="Placement status">
                    <SearchableSelect
                      className="w-full"
                      value={en.placementStatus || ''}
                      onChange={(e) => {
                        const next = [...formData.additionalEnrollments];
                        next[idx] = { ...next[idx], placementStatus: e.target.value };
                        dispatch({ type: 'UPDATE_FIELD', field: 'additionalEnrollments', value: next });
                      }}
                      options={PLACEMENT_STATUS_OPTIONS}
                      placeholder="—"
                    />
                  </InlineField>
                  <InlineField label="Attendance %">
                    <input
                      className={inputClass}
                      value={en.attendancePercentage || ''}
                      onChange={(e) => {
                        const next = [...formData.additionalEnrollments];
                        next[idx] = { ...next[idx], attendancePercentage: e.target.value };
                        dispatch({ type: 'UPDATE_FIELD', field: 'additionalEnrollments', value: next });
                      }}
                    />
                  </InlineField>
                  <InlineField label="Course completion">
                    <input
                      type="date"
                      className={inputClass}
                      value={en.courseCompletionDate || ''}
                      onChange={(e) => {
                        const next = [...formData.additionalEnrollments];
                        next[idx] = { ...next[idx], courseCompletionDate: e.target.value };
                        dispatch({ type: 'UPDATE_FIELD', field: 'additionalEnrollments', value: next });
                      }}
                    />
                  </InlineField>
                  <InlineField label="Remarks">
                    <textarea
                      className={`${inputClass} min-h-[48px]`}
                      value={en.remarks || ''}
                      onChange={(e) => {
                        const next = [...formData.additionalEnrollments];
                        next[idx] = { ...next[idx], remarks: e.target.value };
                        dispatch({ type: 'UPDATE_FIELD', field: 'additionalEnrollments', value: next });
                      }}
                    />
                  </InlineField>
                </div>
              </div>
            ))}
          </CollapsibleSection>
        )}

      {actionSlot}

      {(student?.id || formData.veStudentId) && (
        <p className="text-xs text-gray-500">
          {student?.id ? (
            <>
              Student ID: <span className="font-mono">{student.id}</span>
            </>
          ) : null}
          {student?.unixUsername ? (
            <>
              {student?.id ? ' · ' : null}
              Unix: <span className="font-mono">{student.unixUsername}</span>
            </>
          ) : null}
          {formData.veStudentId ? (
            <>
              {student?.id ? ' · ' : null}
              VE ID: <span className="font-mono">{formData.veStudentId}</span>
            </>
          ) : null}
        </p>
      )}
    </form>
  );
};

export default StudentDetailInlineEdit;
